import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  XMarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

function BulkBlockModal({ isOpen, onClose, doctorId, doctorName, onSuccess }) {
  const [date, setDate] = useState("");
  const [blockType, setBlockType] = useState("multiple"); // 'multiple' or 'all'
  const [slots, setSlots] = useState([]); // Individual slots from API
  const [selectedSlots, setSelectedSlots] = useState([]); // Array of selected slot objects
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [checkingBookings, setCheckingBookings] = useState(false);
  const [affectedBookingsCount, setAffectedBookingsCount] = useState(null);

  // Fetch schedules when date changes
  useEffect(() => {
    if (date && doctorId) {
      fetchSchedules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, doctorId]);

  const fetchSchedules = async () => {
    try {
      const response = await axiosInstance.get(
        `/doctor/${doctorId}/schedules/${date}/slots`
      );

      console.log("Fetched slots:", response.data);

      // Include all slots (available, booked, reserved, and blocked)
      // We'll show blocked slots in red color
      const allSlots = response.data;

      console.log("All slots:", allSlots);
      console.log("Number of slots:", allSlots.length);
      setSlots(allSlots);
    } catch (err) {
      console.error("Error fetching slots:", err);
      setError("Failed to load slots for selected date");
    }
  };

  const checkAffectedBookings = async () => {
    if (!date) {
      setError("Please select a date");
      return;
    }

    if (blockType === "multiple" && selectedSlots.length === 0) {
      setError("Please select at least one slot");
      return;
    }

    setCheckingBookings(true);
    setError("");

    try {
      const response = await axiosInstance.post(`/blockedDate/bulk/check`, {
        doctorId,
        date,
        blockType,
        slots: blockType === "multiple" ? selectedSlots : undefined,
      });

      setAffectedBookingsCount(response.data.affectedBookings);
    } catch (err) {
      console.error("Error checking bookings:", err);
      setError(
        err.response?.data?.error || "Failed to check affected bookings"
      );
    } finally {
      setCheckingBookings(false);
    }
  };

  const handleSubmit = async () => {
    if (!date) {
      setError("Please select a date");
      return;
    }

    if (blockType === "multiple" && selectedSlots.length === 0) {
      setError("Please select at least one slot");
      return;
    }

    if (!reason.trim()) {
      setError("Please provide a reason for blocking");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Use bulk block endpoint for both multiple and all
      const response = await axiosInstance.post(`/blockedDate/bulk`, {
        doctorId,
        date,
        blockType,
        slots: blockType === "multiple" ? selectedSlots : undefined,
        reason,
      });

      onSuccess(response.data.message);
      handleClose();
    } catch (err) {
      console.error("Error blocking slots:", err);
      setError(err.response?.data?.error || "Failed to block slots");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDate("");
    setBlockType("multiple");
    setSelectedSlots([]);
    setReason("");
    setAffectedBookingsCount(null);
    setError("");
    setSlots([]);
    onClose();
  };

  const handleSlotToggle = (slot) => {
    // Toggle the slot (checkbox behavior - can select multiple)
    setSelectedSlots((prev) => {
      const slotKey = `${slot.doctorScheduleId}-${slot.startTime}-${slot.endTime}`;
      const isSelected = prev.some(
        (s) => `${s.doctorScheduleId}-${s.startTime}-${s.endTime}` === slotKey
      );

      if (isSelected) {
        return prev.filter(
          (s) => `${s.doctorScheduleId}-${s.startTime}-${s.endTime}` !== slotKey
        );
      } else {
        return [...prev, slot];
      }
    });
    setAffectedBookingsCount(null); // Reset count when selection changes
  };

  const formatSlotTime = (slot) => {
    return `${slot.startTime} - ${slot.endTime}${
      slot.tokenNumber ? ` (Token #${slot.tokenNumber})` : ""
    }`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="w-7 h-7 text-red-600" />
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                Block Schedule(s)
              </h2>
              <p className="text-xs sm:text-sm text-gray-600">
                Block specific schedules or entire day
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 p-4 rounded">
            <p className="text-sm text-blue-800">
              <strong>Doctor:</strong> {doctorName}
            </p>
          </div>

          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setAffectedBookingsCount(null);
              }}
              min={new Date().toISOString().split("T")[0]}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Block Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Block Type <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <label className="flex items-center p-3 hover:bg-gray-50 rounded cursor-pointer border border-gray-200">
                <input
                  type="radio"
                  value="multiple"
                  checked={blockType === "multiple"}
                  onChange={(e) => {
                    setBlockType(e.target.value);
                    setSelectedSlots([]);
                    setAffectedBookingsCount(null);
                  }}
                  className="mr-3"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    Select Specific Time Slots
                  </span>
                  <p className="text-xs text-gray-500">
                    Choose one or multiple time slots to block (checkboxes)
                  </p>
                </div>
              </label>
              <label className="flex items-center p-3 hover:bg-gray-50 rounded cursor-pointer border border-gray-200">
                <input
                  type="radio"
                  value="all"
                  checked={blockType === "all"}
                  onChange={(e) => {
                    setBlockType(e.target.value);
                    setSelectedSlots([]);
                    setAffectedBookingsCount(null);
                  }}
                  className="mr-3"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    Block Entire Day
                  </span>
                  <p className="text-xs text-gray-500">
                    Block all schedules for this doctor on this day
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Slot Selection (for 'multiple' type only) */}
          {blockType === "multiple" && slots.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Time Slots to Block{" "}
                <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500 ml-2">
                  ({slots.length} available)
                </span>
              </label>
              <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 mb-2">
                <p className="text-xs text-blue-700">
                  ✓ You can select one or multiple time slots
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  💡 Booked/Reserved slots can be selected to cancel
                  appointments. Already blocked slots cannot be selected.
                </p>
              </div>
              <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                {slots.map((slot, index) => {
                  const slotKey = `${slot.doctorScheduleId}-${slot.startTime}-${slot.endTime}`;
                  const isSelected = selectedSlots.some(
                    (s) =>
                      `${s.doctorScheduleId}-${s.startTime}-${s.endTime}` ===
                      slotKey
                  );

                  // Check if slot is blocked, booked, or reserved
                  const isBlocked = slot.blocked;
                  const isBooked = slot.booked;
                  const isReserved = slot.reserved;
                  // Only disable already blocked slots - allow selecting booked/reserved slots to cancel them
                  const isDisabled = isBlocked;

                  // Determine styling based on status
                  let statusBgColor = "";
                  let statusTextColor = "text-gray-800";
                  let statusLabel = "";
                  let statusLabelColor = "";

                  if (isBlocked) {
                    statusBgColor = "bg-red-50";
                    statusTextColor = "text-red-700";
                    statusLabel = "Blocked";
                    statusLabelColor = "text-red-600 bg-red-100";
                  } else if (isBooked) {
                    statusBgColor = "bg-gray-50";
                    statusTextColor = "text-gray-500";
                    statusLabel = "Booked";
                    statusLabelColor = "text-gray-600 bg-gray-100";
                  } else if (isReserved) {
                    statusBgColor = "bg-yellow-50";
                    statusTextColor = "text-yellow-700";
                    statusLabel = "Reserved";
                    statusLabelColor = "text-yellow-600 bg-yellow-100";
                  }

                  return (
                    <label
                      key={`${slotKey}-${index}`}
                      className={`flex items-center p-3 border-b last:border-b-0 ${statusBgColor} ${
                        isDisabled
                          ? "cursor-not-allowed opacity-75"
                          : "hover:bg-gray-50 cursor-pointer"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => !isDisabled && handleSlotToggle(slot)}
                        disabled={isDisabled}
                        className="mr-3 w-4 h-4 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p
                            className={`text-sm font-medium ${statusTextColor}`}
                          >
                            {formatSlotTime(slot)}
                          </p>
                          {statusLabel && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full ${statusLabelColor}`}
                            >
                              {statusLabel}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {slot.departmentName} - {slot.type}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {blockType === "multiple" && slots.length === 0 && date && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
              No slots found for the selected date
            </div>
          )}

          {blockType === "multiple" && slots.length > 0 && date && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-3 py-2 rounded text-xs mt-2">
              <p>
                <strong>Legend:</strong>
                <span className="ml-2">
                  <span className="inline-block w-3 h-3 bg-red-100 border border-red-300 rounded mr-1"></span>
                  Blocked slots shown in red
                </span>
                <span className="ml-2">
                  <span className="inline-block w-3 h-3 bg-gray-100 border border-gray-300 rounded mr-1"></span>
                  Booked slots in gray
                </span>
              </p>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Blocking <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Personal leave, Training session, etc."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Check Bookings Button */}
          <button
            onClick={checkAffectedBookings}
            disabled={checkingBookings || !date}
            className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-300 text-white py-2 px-4 rounded-lg transition text-sm sm:text-base"
          >
            {checkingBookings ? "Checking..." : "Check Affected Bookings"}
          </button>

          {/* Affected Bookings Info */}
          {affectedBookingsCount !== null && (
            <div
              className={`p-4 rounded-lg ${
                affectedBookingsCount > 0
                  ? "bg-orange-50 border border-orange-200"
                  : "bg-green-50 border border-green-200"
              }`}
            >
              <p
                className={`text-sm font-medium ${
                  affectedBookingsCount > 0
                    ? "text-orange-800"
                    : "text-green-800"
                }`}
              >
                {affectedBookingsCount > 0
                  ? `⚠️ ${affectedBookingsCount} patient(s) will be notified and their bookings cancelled`
                  : "✓ No bookings affected"}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="w-full sm:w-auto px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || affectedBookingsCount === null}
            className="w-full sm:w-auto bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white px-6 py-2 rounded-lg transition text-sm sm:text-base"
          >
            {loading ? "Blocking..." : "Confirm & Block"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkBlockModal;
